import { CHAIN_ELLIPSE_RATIO_DEFAULT, isAsymmetric, isGuideSnap, SINGLE_CROCHET_RATIO_DEFAULT, type Tbc, type TbcPoint, type TbcType } from './tbc-types';
import { docDefaultWidthScale, type CrochetSizingDefaults } from './sizing-defaults';

/** Minimum leg length (world units) — bounds the rect's half-height so resize
 *  can't collapse the stitch to zero. */
export const MIN_RECT_LEG = 4;
/** Minimum widthScale — bounds the rect's width on left/right edge resize. */
export const MIN_RECT_WIDTH_SCALE = 0.1;
/** Ceiling applied ONLY to the doc-default widthScale tier (not per-instance
 *  values — gizmo drags may legitimately exceed this). Matches the settings
 *  field's advertised max in sizing-defaults-catalog.ts, which imports this
 *  constant so the two can't drift apart. */
export const MAX_RECT_DOC_WIDTH_SCALE = 5;

/** Union of TBC types edited via the rect transform gizmo. Add a member here
 *  (plus a RECT_TBC_CONFIG entry and a RECT_GLYPH_DRAWERS entry) to introduce a
 *  new rect type. Every type except the leg-cap family (double/half-double/treble crochet)
 *  is rect-edited when symmetric. */
export type RectTbcType =
    | 'slip-stitch'
    | 'chain' | 'chain-full'
    | 'single-crochet' | 'single-crochet-full'
    | 'single-crochet-x' | 'single-crochet-x-full';

/** Data-driven membership: types edited via the rect transform gizmo. */
export const RECT_TBC_TYPES: ReadonlySet<TbcType> = new Set<TbcType>([
    'slip-stitch',
    'chain', 'chain-full',
    'single-crochet', 'single-crochet-full',
    'single-crochet-x', 'single-crochet-x-full',
]);

export function isRectType(type: TbcType): type is RectTbcType {
    return RECT_TBC_TYPES.has(type);
}

/** Per-INSTANCE dispatch rule for rect editing: rect-typed AND symmetric.
 *  Asymmetric stitches (increase/decrease legs) always stay on the shaped
 *  rendering/interaction path — the gizmo, geometry and drag math assume
 *  exactly one base and one top. Top/base counts are immutable after
 *  creation, so the rect-vs-shaped decision is stable per stitch. */
export function isRectTbc(t: Tbc): boolean {
    return isRectType(t.type) && !isAsymmetric(t);
}

/** How a rect type maps its anchors onto the rectangle:
 *  - 'center': base = rect center, top = mid-top-edge; height = 2·legLength
 *    (slip-stitch).
 *  - 'span':   base = mid-BOTTOM-edge, top = mid-top-edge; the rect spans
 *    base→top, so height = legLength and the center is their midpoint
 *    (-full shaped variants). */
export type RectAnchorMode = 'center' | 'span';

/** Per-type rect policy: anchor mode + the default widthScale used by the rect
 *  width formula (width = widthScale·legLength). Center-anchored midpoint
 *  variants carry a 2× default vs their span sibling so they match the legacy
 *  shaped `2·b·widthScale` rendering; span variants keep the shaped ratio
 *  unchanged (the span formula already equals it). This is intentionally
 *  separate from tbc-types' `defaultWidthScale`, which still serves the shaped
 *  (asymmetric) renderer and must NOT double. */
export const RECT_TBC_CONFIG: Record<RectTbcType, { anchor: RectAnchorMode; defaultWidthScale: number }> = {
    'slip-stitch': { anchor: 'center', defaultWidthScale: 2 }, // its own square-dot ratio, not a shaped family
    'chain': { anchor: 'center', defaultWidthScale: 2 * CHAIN_ELLIPSE_RATIO_DEFAULT },
    'chain-full': { anchor: 'span', defaultWidthScale: CHAIN_ELLIPSE_RATIO_DEFAULT },
    'single-crochet': { anchor: 'center', defaultWidthScale: 2 * SINGLE_CROCHET_RATIO_DEFAULT },
    'single-crochet-full': { anchor: 'span', defaultWidthScale: SINGLE_CROCHET_RATIO_DEFAULT },
    'single-crochet-x': { anchor: 'center', defaultWidthScale: 2 * SINGLE_CROCHET_RATIO_DEFAULT },
    'single-crochet-x-full': { anchor: 'span', defaultWidthScale: SINGLE_CROCHET_RATIO_DEFAULT },
};

/** Returns the anchor mode for the given type. The `'center'` fallback exists
 *  for un-narrowed call sites — callers should only reach it for rect types
 *  (the fallback path is a safety net, not an intended code path). */
export function rectAnchorMode(type: TbcType): RectAnchorMode {
    return isRectType(type) ? RECT_TBC_CONFIG[type].anchor : 'center';
}

/** The default widthScale a rect type renders at when no per-instance
 *  widthScale is set — the rect-side counterpart to tbc-types'
 *  `defaultWidthScale` (which serves the shaped/asymmetric renderer). */
export function rectDefaultWidthScale(type: TbcType): number {
    return RECT_TBC_CONFIG[type as RectTbcType].defaultWidthScale;
}

/** Effective widthScale for a rect TBC instance: its per-instance override >
 *  the document's per-type default (clamped to [MIN_RECT_WIDTH_SCALE,
 *  MAX_RECT_DOC_WIDTH_SCALE]) > the rect default for its type. Callers must
 *  have a rect-typed stitch. */
export function rectWidthScale(t: Tbc, dd: CrochetSizingDefaults | undefined): number {
    if (t.widthScale != null) return t.widthScale;
    const doc = docDefaultWidthScale(dd, t.type);
    if (doc != null) return Math.min(MAX_RECT_DOC_WIDTH_SCALE, Math.max(MIN_RECT_WIDTH_SCALE, doc));
    return rectDefaultWidthScale(t.type);
}

/** Desired world widths for rect stitches sorted by their along-guide
 *  coordinate, so each stitch grows/shrinks to meet its nearest neighbor
 *  without moving. Rect width is symmetric about the stitch center, so a stitch
 *  can fully fill only its tighter side (interior = min of the two gaps).
 *  - Open guides (line, arc): the first/last stitch mirrors its single inner gap.
 *  - Cyclic guides (concentric-circles): first/last wrap as neighbors using
 *    `period` (the ring circumference); every stitch uses min(left, right).
 *  Assumes `coords` is sorted ascending and has length >= 2. */
export function rewidthFillWidths(coords: number[], cyclic: boolean, period?: number): number[] {
    const n = coords.length;
    const out = new Array<number>(n);
    for (let i = 0; i < n; i++) {
        if (cyclic) {
            const p = period as number;
            const left = i > 0 ? coords[i] - coords[i - 1] : coords[0] - (coords[n - 1] - p);
            const right = i < n - 1 ? coords[i + 1] - coords[i] : coords[0] + p - coords[n - 1];
            out[i] = Math.min(left, right);
        } else {
            const left = i > 0 ? coords[i] - coords[i - 1] : Infinity;
            const right = i < n - 1 ? coords[i + 1] - coords[i] : Infinity;
            if (i === 0) out[i] = right;          // first: mirror the single (right) gap
            else if (i === n - 1) out[i] = left;  // last: mirror the single (left) gap
            else out[i] = Math.min(left, right);  // interior: fill the tighter side
        }
    }
    return out;
}

export type RectGizmoGeometry = {
    base: TbcPoint;   // base anchor (rect center in 'center' mode, mid-bottom-edge in 'span')
    top: TbcPoint;    // top anchor (mid of top edge in both modes)
    center: TbcPoint; // geometric rect center
    halfH: number;    // half-height along `up`
    up: TbcPoint;     // unit vector base→top
    right: TbcPoint;  // unit vector, up rotated +90°
    angle: number;    // atan2(top - base)
    legLength: number;
    width: number;    // widthScale * legLength
    corners: { tl: TbcPoint; tr: TbcPoint; br: TbcPoint; bl: TbcPoint };
    edges: { top: TbcPoint; right: TbcPoint; bottom: TbcPoint; left: TbcPoint };
    rotationHandle: TbcPoint;
};

const add = (p: TbcPoint, v: TbcPoint, s: number): TbcPoint => ({ x: p.x + v.x * s, y: p.y + v.y * s });

/** Resolve the gizmo geometry for a rect TBC. Single base/top — callers must
 *  have confirmed isRectTbc(t) (asymmetric rect-typed stitches never reach the
 *  gizmo path). `rotationHandleOffset` is the world-space distance the
 *  rotation handle sits beyond the top edge (caller passes screenPx / zoom).
 *  The anchor mode comes from `RECT_TBC_CONFIG`. */
export function rectGizmoGeometry(t: Tbc, rotationHandleOffset: number, dd: CrochetSizingDefaults | undefined): RectGizmoGeometry {
    const widthScale = rectWidthScale(t, dd);
    return rectGizmoGeometryCore(
        t.bases[0].point, t.tops[0].point, widthScale,
        rectAnchorMode(t.type), rotationHandleOffset,
    );
}

/** Pure geometry resolver — exported separately so anchor modes can be unit
 *  tested without a Tbc. All handle positions derive from (center, halfH,
 *  halfW) in the (up, right) frame. */
export function rectGizmoGeometryCore(
    base: TbcPoint,
    top: TbcPoint,
    widthScale: number,
    anchor: RectAnchorMode,
    rotationHandleOffset: number,
): RectGizmoGeometry {
    const dx = top.x - base.x;
    const dy = top.y - base.y;
    const legLength = Math.hypot(dx, dy);
    // Degenerate guard: default to screen-up. (Engine clamps legLength ≥ MIN_RECT_LEG,
    // so this only matters for malformed input.)
    const up: TbcPoint = legLength > 1e-9 ? { x: dx / legLength, y: dy / legLength } : { x: 0, y: -1 };
    const right: TbcPoint = { x: -up.y, y: up.x };
    const width = widthScale * legLength;
    const halfW = width / 2;
    const halfH = anchor === 'center' ? legLength : legLength / 2;
    const center: TbcPoint = anchor === 'center'
        ? base
        : { x: (base.x + top.x) / 2, y: (base.y + top.y) / 2 };
    const angle = Math.atan2(up.y, up.x);

    const topMid = add(center, up, halfH);
    const bottomMid = add(center, up, -halfH);
    const rightMid = add(center, right, halfW);
    const leftMid = add(center, right, -halfW);

    return {
        base, top, center, halfH, up, right, angle, legLength, width,
        corners: {
            tl: add(topMid, right, -halfW),
            tr: add(topMid, right, halfW),
            br: add(bottomMid, right, halfW),
            bl: add(bottomMid, right, -halfW),
        },
        edges: { top: topMid, right: rightMid, bottom: bottomMid, left: leftMid },
        rotationHandle: add(topMid, up, rotationHandleOffset),
    };
}

/** Inscribed ellipse as a closed polygon: rx = width/2, ry = halfH, centered
 *  at the rect center, rotated by the leg angle (in center mode this equals the
 *  legacy base/legLength behavior). Square rect ⇒ circle (the slip-stitch dot);
 *  non-square ⇒ oval. `inflate` (world units) grows both radii — used to draw the
 *  selection halo slightly larger than the body so a ring peeks out. */
export function rectEllipsePolygon(geom: RectGizmoGeometry, segments: number, inflate = 0): TbcPoint[] {
    const rx = geom.width / 2 + inflate;
    const ry = geom.halfH + inflate;
    const pts: TbcPoint[] = [];
    for (let i = 0; i < segments; i++) {
        const a = (i / segments) * Math.PI * 2;
        const lx = Math.cos(a) * rx; // local: along `right`
        const ly = Math.sin(a) * ry; // local: along `up`
        pts.push({
            x: geom.center.x + geom.right.x * lx + geom.up.x * ly,
            y: geom.center.y + geom.right.y * lx + geom.up.y * ly,
        });
    }
    return pts;
}

/** The two stroke segments of a `+` glyph inscribed in the gizmo rect: a
 *  vertical arm spanning ±halfH along `up` and a horizontal arm spanning
 *  ±(width/2) along `right`, both through the rect center. Pure — callers
 *  apply heightScale by passing a geom whose halfH is pre-scaled. */
export function rectPlusSegments(geom: RectGizmoGeometry): Array<[TbcPoint, TbcPoint]> {
    const halfW = geom.width / 2;
    return [
        [add(geom.center, geom.up, -geom.halfH), add(geom.center, geom.up, geom.halfH)],
        [add(geom.center, geom.right, -halfW), add(geom.center, geom.right, halfW)],
    ];
}

/** The two corner-to-corner diagonals of the gizmo rect — the `×` glyph (TL→BR,
 *  TR→BL). Corners are recomputed from `halfH` rather than read off `geom.corners`
 *  on purpose: callers apply heightScale by passing a geom with a pre-scaled
 *  `halfH` (its `corners` field is stale), so reusing `geom.corners` would ignore
 *  heightScale. */
export function rectCrossSegments(geom: RectGizmoGeometry): Array<[TbcPoint, TbcPoint]> {
    const halfW = geom.width / 2;
    const tl = add(add(geom.center, geom.up, geom.halfH), geom.right, -halfW);
    const tr = add(add(geom.center, geom.up, geom.halfH), geom.right, halfW);
    const bl = add(add(geom.center, geom.up, -geom.halfH), geom.right, -halfW);
    const br = add(add(geom.center, geom.up, -geom.halfH), geom.right, halfW);
    return [[tl, br], [tr, bl]];
}

// ---------------------------------------------------------------------------
// Drag math — pure functions used by the rect gizmo interaction handlers
// ---------------------------------------------------------------------------

const dot = (ax: number, ay: number, bx: number, by: number): number => ax * bx + ay * by;

/** Aspect-locked uniform scale about `pivot` (the pivot holds still; both
 *  anchors scale away from / toward it). The scale factor is the pointer's
 *  distance from the pivot over the grabbed corner's distance from it. When the
 *  pivot is the geometric center every corner yields the same factor (the center
 *  is equidistant from all corners); when it's a pinned endpoint the factor
 *  varies per corner. Returns the new base/top along the unchanged leg
 *  direction. The caller picks the pivot via {@link rectTransformPivot}. */
export function cornerScaleAnchors(
    base: TbcPoint,
    top: TbcPoint,
    widthScale: number,
    anchor: RectAnchorMode,
    corner: RectCorner,
    pointer: TbcPoint,
    pivot: TbcPoint,
): { base: TbcPoint; top: TbcPoint } {
    const geom = rectGizmoGeometryCore(base, top, widthScale, anchor, 0);
    const legLength = geom.legLength || MIN_RECT_LEG;
    const grabbed = geom.corners[corner];
    const ref = Math.hypot(grabbed.x - pivot.x, grabbed.y - pivot.y);
    const f = Math.hypot(pointer.x - pivot.x, pointer.y - pivot.y) / (ref || 1);
    // Clamp the factor so the resulting leg can't collapse below MIN_RECT_LEG
    // (both anchors scale about the pivot, so the leg scales by the factor).
    const cf = Math.max(MIN_RECT_LEG / legLength, f);
    const scaleAboutPivot = (p: TbcPoint): TbcPoint => ({
        x: pivot.x + (p.x - pivot.x) * cf,
        y: pivot.y + (p.y - pivot.y) * cf,
    });
    return { base: scaleAboutPivot(base), top: scaleAboutPivot(top) };
}

/** Height-only scale about `pivot`, preserving ABSOLUTE width. The dragged edge
 *  (`side`) follows the pointer along the leg (`up`) axis; both anchors scale
 *  about the pivot so its leg direction is preserved. widthScale is rescaled to
 *  hold world-space width constant. If the dragged edge sits on the pivot (its
 *  distance along `up` is ~0 — e.g. grabbing the pinned endpoint's own edge),
 *  the drag is a no-op. */
export function heightScaleAnchors(
    base: TbcPoint,
    top: TbcPoint,
    widthScale: number,
    anchor: RectAnchorMode,
    pointer: TbcPoint,
    pivot: TbcPoint,
    side: 'top' | 'bottom',
): { base: TbcPoint; top: TbcPoint; widthScale: number } {
    const geom = rectGizmoGeometryCore(base, top, widthScale, anchor, 0);
    const { up } = geom;
    const legLength = geom.legLength || MIN_RECT_LEG;
    const oldWidth = widthScale * legLength;
    const edgeMid = side === 'top' ? geom.edges.top : geom.edges.bottom;
    // Reference: the dragged edge's distance from the pivot along `up`.
    const ref = Math.abs(dot(edgeMid.x - pivot.x, edgeMid.y - pivot.y, up.x, up.y));
    if (ref < 1e-6) return { base, top, widthScale }; // dragged edge is on the pivot → no-op
    const d = Math.abs(dot(pointer.x - pivot.x, pointer.y - pivot.y, up.x, up.y));
    const s = Math.max(MIN_RECT_LEG / legLength, d / ref);
    const newLeg = legLength * s;
    const scale = (p: TbcPoint): TbcPoint => ({
        x: pivot.x + (p.x - pivot.x) * s,
        y: pivot.y + (p.y - pivot.y) * s,
    });
    return {
        base: scale(base),
        top: scale(top),
        widthScale: Math.max(MIN_RECT_WIDTH_SCALE, oldWidth / newLeg),
    };
}

/** Left/right edge drag → width only. legLength (the leg/top) is unchanged;
 *  widthScale is set from the pointer's perpendicular distance to the axis. */
export function edgeResizeWidth(base: TbcPoint, top: TbcPoint, pointer: TbcPoint): { widthScale: number } {
    const dx = top.x - base.x;
    const dy = top.y - base.y;
    const legLength = Math.hypot(dx, dy) || MIN_RECT_LEG;
    const ux = dx / legLength;
    const uy = dy / legLength;
    const rx = -uy; // right = up rotated +90°
    const ry = ux;
    const halfW = Math.abs(dot(pointer.x - base.x, pointer.y - base.y, rx, ry));
    return { widthScale: Math.max(MIN_RECT_WIDTH_SCALE, (2 * halfW) / legLength) };
}

/** Rotate the rect's anchors about `pivot` by `delta` radians. A pivot equal to
 *  an endpoint leaves that endpoint fixed and orbits the other; a pivot at the
 *  geometric center spins the stitch in place. The caller picks the pivot via
 *  {@link rectTransformPivot}. */
export function rotateRectAnchors(
    base: TbcPoint,
    top: TbcPoint,
    pivot: TbcPoint,
    delta: number,
): { base: TbcPoint; top: TbcPoint } {
    const c = Math.cos(delta);
    const s = Math.sin(delta);
    const rot = (p: TbcPoint): TbcPoint => {
        const dx = p.x - pivot.x;
        const dy = p.y - pivot.y;
        return { x: pivot.x + dx * c - dy * s, y: pivot.y + dx * s + dy * c };
    };
    return { base: rot(base), top: rot(top) };
}

/** Resolve the transform pivot for a rect TBC from its snap state:
 *  - base pinned (ANY snap — guide or parent-attach) → pivot at the base
 *  - top pinned (GUIDE snap only) → pivot at the top
 *  - both pinned → 'both': only width is adjustable (rotation/scale would have
 *    to move a pinned endpoint), so the engine disables those transforms
 *  - neither pinned → the geometric center (base in 'center' mode, the
 *    base/top midpoint in 'span'), i.e. the default free-floating behavior. */
export type RectTransformPivot =
    | { kind: 'both' }
    | { kind: 'base' | 'top' | 'center'; point: TbcPoint };

export function rectTransformPivot(t: Tbc): RectTransformPivot {
    const base = t.bases[0];
    const top = t.tops[0];
    const basePinned = base.snap != null;
    const topPinned = isGuideSnap(top.snap);
    if (basePinned && topPinned) return { kind: 'both' };
    if (basePinned) return { kind: 'base', point: base.point };
    if (topPinned) return { kind: 'top', point: top.point };
    const center: TbcPoint = rectAnchorMode(t.type) === 'center'
        ? base.point
        : { x: (base.point.x + top.point.x) / 2, y: (base.point.y + top.point.y) / 2 };
    return { kind: 'center', point: center };
}

// ---------------------------------------------------------------------------
// Size factors — bulk numeric width/height editing (1.0 = the type's natural
// size). Width factor maps to widthScale; height factor maps to legLength.
// Pure and parameterized by (baseLeg, naturalWS) so callers inject the per-type
// natural leg length and widthScale without this module depending on the leg
// tables (which would form an import cycle with stitch-sizing).
// ---------------------------------------------------------------------------

/** Factor bounds shared with the stitch size controls (MIN/MAX_STITCH_*_SCALE in stitch-sizing). */
export const MIN_RECT_SIZE_FACTOR = 0.25;
export const MAX_RECT_SIZE_FACTOR = 3;

/** Clamp a size factor to [MIN_RECT_SIZE_FACTOR, MAX_RECT_SIZE_FACTOR];
 *  non-finite input falls back to 1 (natural). */
export function clampRectSizeFactor(f: number): number {
    if (!Number.isFinite(f)) return 1;
    return Math.max(MIN_RECT_SIZE_FACTOR, Math.min(MAX_RECT_SIZE_FACTOR, f));
}

export type RectSizeFactors = { widthFactor: number; heightFactor: number };

/** Read the unitless (1.0 = natural) width/height factors of a rect TBC.
 *  `baseLeg` = the type's natural leg length, `naturalWS` = its natural
 *  widthScale. heightFactor = legLength / baseLeg; widthFactor = rendered world
 *  width over the natural world width (so it is independent of leg length only
 *  when widthScale was set to compensate — i.e. it reflects what is drawn).
 *  Assumes legLength > 0 (the engine clamps it at MIN_RECT_LEG); a degenerate
 *  zero-leg stitch reads back as { widthFactor: 0, heightFactor: 0 }. */
export function rectSizeFactors(t: Tbc, baseLeg: number, naturalWS: number, dd: CrochetSizingDefaults | undefined): RectSizeFactors {
    const base = t.bases[0].point;
    const top = t.tops[0].point;
    const legLength = Math.hypot(top.x - base.x, top.y - base.y);
    const ws = rectWidthScale(t, dd);
    const heightFactor = baseLeg > 0 ? legLength / baseLeg : 1;
    const widthFactor = naturalWS > 0 && baseLeg > 0
        ? (ws * legLength) / (naturalWS * baseLeg)
        : 1;
    return { widthFactor, heightFactor };
}

/** widthScale that renders `clamp(factor)× naturalWS·baseLeg` of world width at
 *  the stitch's CURRENT leg — i.e. width independent of height. Leg is left
 *  untouched by width edits. Floored at MIN_RECT_WIDTH_SCALE. */
export function rectWidthScaleForFactor(t: Tbc, factor: number, baseLeg: number, naturalWS: number): number {
    const base = t.bases[0].point;
    const top = t.tops[0].point;
    const legLength = Math.hypot(top.x - base.x, top.y - base.y) || MIN_RECT_LEG;
    const worldWidth = clampRectSizeFactor(factor) * naturalWS * baseLeg;
    return Math.max(MIN_RECT_WIDTH_SCALE, worldWidth / legLength);
}

/** New anchors + widthScale for a target height factor: leg becomes
 *  `clamp(factor)·baseLeg` (floored at MIN_RECT_LEG), anchors scale about the
 *  snap-aware pivot, and widthScale is rescaled to keep ABSOLUTE world width
 *  constant (mirrors heightScaleAnchors). Returns null when both endpoints are
 *  pinned (pivot.kind === 'both') — that stitch's height cannot change. */
export function rectAnchorsForHeightFactor(
    t: Tbc,
    factor: number,
    baseLeg: number,
    dd: CrochetSizingDefaults | undefined,
): { base: TbcPoint; top: TbcPoint; widthScale: number } | null {
    const pivot = rectTransformPivot(t);
    if (pivot.kind === 'both') return null;
    const base = t.bases[0].point;
    const top = t.tops[0].point;
    const legLength = Math.hypot(top.x - base.x, top.y - base.y) || MIN_RECT_LEG;
    const targetLeg = Math.max(MIN_RECT_LEG, clampRectSizeFactor(factor) * baseLeg);
    const s = targetLeg / legLength;
    const p = pivot.point;
    const scale = (q: TbcPoint): TbcPoint => ({ x: p.x + (q.x - p.x) * s, y: p.y + (q.y - p.y) * s });
    const oldWidth = rectWidthScale(t, dd) * legLength;
    return {
        base: scale(base),
        top: scale(top),
        widthScale: Math.max(MIN_RECT_WIDTH_SCALE, oldWidth / targetLeg),
    };
}

// ---------------------------------------------------------------------------
// Cursor quantization — which resize cursor a gizmo handle should show
// ---------------------------------------------------------------------------

export type RectCorner = 'tl' | 'tr' | 'br' | 'bl';

/** The four standard resize-cursor axes (screen coords, y down):
 *  ew = →, nwse = ↘, ns = ↓, nesw = ↙. */
export type ResizeCursorAxis = 'ns' | 'ew' | 'nesw' | 'nwse';

export type RectGizmoResizeHandle =
    | { kind: 'corner'; corner: RectCorner }
    | { kind: 'edge'; side: 'left' | 'right' | 'top' | 'bottom' };

/** Quantize a resize handle's drag axis to the nearest standard cursor axis
 *  (45° buckets, half-open at the lower bound). Drag axes: corners read as the
 *  corner's *unit* quadrant diagonal in the rect's local (right, up) frame,
 *  rotated into world space — NOT the raw center→corner vector, whose angle
 *  bends with aspect ratio (an elongated rect would flatten a corner toward
 *  ns/ew). Rotation is still honored: a 45°-rotated square's corner diagonal
 *  genuinely points along an axis, so it resolves to ns/ew. Edges are draggable
 *  along their whole length: left/right scale width (along `right`), top/bottom
 *  scale height (along `up`). The anchor circles sitting on the top/bottom edge
 *  midpoints (top anchor; base anchor in 'span') take priority there, so the
 *  height zone is the rest of the edge. */
export function rectGizmoCursorAxis(geom: RectGizmoGeometry, handle: RectGizmoResizeHandle): ResizeCursorAxis {
    let dir: TbcPoint;
    if (handle.kind === 'edge') {
        dir = handle.side === 'top' || handle.side === 'bottom' ? geom.up : geom.right;
    } else {
        const sx = handle.corner === 'tr' || handle.corner === 'br' ? 1 : -1; // along `right`
        const sy = handle.corner === 'tl' || handle.corner === 'tr' ? 1 : -1; // along `up`
        dir = { x: sx * geom.right.x + sy * geom.up.x, y: sx * geom.right.y + sy * geom.up.y };
    }
    const deg = (Math.atan2(dir.y, dir.x) * 180) / Math.PI;
    const a = ((deg % 180) + 180) % 180; // undirected axis angle in [0, 180)
    if (a < 22.5 || a >= 157.5) return 'ew';
    if (a < 67.5) return 'nwse';
    if (a < 112.5) return 'ns';
    return 'nesw';
}
